import { updateSEO } from './seo'
import { initScrollReveal, refreshScrollReveal } from './scrollReveal'
import { parsePath, toLogicalPath, withLangPrefix, type Lang } from './lang'
import { localizeAnchors } from './localizeAnchors'
import { trackPageView } from './analytics'

type RouteHandler = () => HTMLElement | Promise<HTMLElement>

interface Route {
  path: string
  handler: RouteHandler
  title: string
  description: string
}

class Router {
  private routes: Route[] = []
  /** Active language, derived from the URL at init(); fixed for the page's life. */
  private currentLang: Lang = 'en'
  /**
   * The first render replaces prerendered markup that is already on screen and
   * already correct, so it skips the fade-out — otherwise the page blanks while
   * the route's chunk loads. Later navigations keep the transition.
   */
  private firstRender = true
  private notFoundHandler: RouteHandler = () => {
    const div = document.createElement('div')
    div.innerHTML = '<h1>404 - Page Not Found</h1>'
    return div
  }

  addRoute(path: string, handler: RouteHandler, title: string, description: string) {
    this.routes.push({ path, handler, title, description })
  }

  setNotFoundHandler(handler: RouteHandler) {
    this.notFoundHandler = handler
  }

  getCurrentLang(): Lang {
    return this.currentLang
  }

  /**
   * Navigate to a LOGICAL path (no /ko prefix). The router applies the active
   * language prefix when writing history, matches routes by logical path, and
   * passes language + logical path to the SEO layer.
   */
  async navigate(path: string, pushState: boolean = true) {
    const logicalPath = toLogicalPath(path)
    window.dispatchEvent(new CustomEvent('route-will-change', { detail: logicalPath }))

    if (pushState) {
      history.pushState({}, '', withLangPrefix(logicalPath, this.currentLang))
    }

    const route = this.routes.find(r => r.path === logicalPath)
    const handler = route?.handler || this.notFoundHandler

    // Update SEO
    if (route) {
      updateSEO({
        title: route.title,
        description: route.description,
        path: logicalPath,
        lang: this.currentLang,
      })
    }

    // Track SPA page_view in GA4 (no-op until the visitor grants consent).
    // Fired here, after updateSEO sets the title and before the render
    // early-return, so it is independent of render success. The initial load
    // is covered because init() calls navigate().
    trackPageView()

    // Page transition
    const pageRoot = document.getElementById('page-root')
    if (!pageRoot) return

    // Start the handler BEFORE the fade so a lazily-imported route chunk
    // downloads during the 200ms transition instead of after it. Route handlers
    // are dynamic imports, so this is where the network request begins.
    const contentPromise = Promise.resolve(handler())

    if (this.firstRender) {
      // Prerendered markup is on screen and correct; leave it visible and swap
      // in place once the chunk resolves.
      this.firstRender = false
      pageRoot.style.transition = 'opacity 0.2s ease-in-out'
    } else {
      pageRoot.style.opacity = '0'
      pageRoot.style.transition = 'opacity 0.2s ease-in-out'
      await new Promise(resolve => setTimeout(resolve, 200))
    }

    // Render new page
    const content = await contentPromise
    pageRoot.innerHTML = ''
    pageRoot.appendChild(content)

    // Make freshly-rendered internal links language-aware (no-op for English)
    localizeAnchors(pageRoot, this.currentLang)

    // Fade in
    pageRoot.style.opacity = '1'

    // Scroll to top
    window.scrollTo(0, 0)
    window.dispatchEvent(new CustomEvent('route-rendered', { detail: logicalPath }))

    // Apply scroll reveal animations after a short delay
    setTimeout(() => {
      refreshScrollReveal()
    }, 100)
  }

  init() {
    // Initialize scroll reveal observer
    initScrollReveal()

    const { lang, logicalPath } = parsePath(window.location.pathname)
    this.currentLang = lang
    this.navigate(logicalPath, false)
  }

  /**
   * Handle browser back/forward. If the popped URL is in a different language
   * than the one this page was loaded with, a full reload is required (the
   * persistent Header/Footer were built for the original language).
   */
  handlePopState() {
    const { lang, logicalPath } = parsePath(window.location.pathname)
    if (lang !== this.currentLang) {
      window.location.reload()
      return
    }
    this.navigate(logicalPath, false)
  }
}

export const router = new Router()
